import React from 'react';
import PropTypes from 'prop-types';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

const categoryLabels = {
  // Polymet prototype mock keys
  operations: 'Operations',
  financial_health: 'Financial Health',
  human_resources: 'Human Resources',
  customer_concentration: 'Customer Concentration',
  growth_strategy: 'Growth Strategy',
  succession_plan: 'Succession Plan',
  // Production CEPA dimension keys
  financial: 'Financial Capital',
  human: 'Human Capital',
  structural: 'Structural Capital',
  customer: 'Customer Capital',
  wealth: 'Wealth Gap',
};

const gapMeanings = {
  // Polymet keys
  operations: 'Operational inefficiencies may reduce buyer confidence and valuation.',
  financial_health: 'Weak financials limit buyer pool and financing options.',
  human_resources: 'Key person risk and HR gaps threaten post-sale continuity.',
  customer_concentration: 'High concentration increases perceived risk and lowers multiples.',
  growth_strategy: 'Lack of growth trajectory limits strategic buyer interest.',
  succession_plan: 'No succession plan signals unpreparedness and complicates transition.',
  // Production CEPA keys
  financial: 'Weak financial capital limits buyer pool, financing options, and valuation multiples.',
  human: 'Key-person risk and management depth gaps threaten post-sale continuity.',
  structural: 'Undocumented processes and weak systems reduce buyer confidence and transferability.',
  customer: 'High customer concentration increases perceived risk and lowers multiples.',
  wealth: 'Wealth gap signals owner reliance on the business — sale may not meet personal targets.',
};

const strengthMeanings = {
  // Polymet keys
  operations: 'Strong operations support smooth transition and buyer confidence.',
  financial_health: 'Solid financials attract buyers and maximize valuation.',
  human_resources: 'Strong team reduces key person risk and ensures continuity.',
  customer_concentration: 'Diversified customer base reduces risk and increases value.',
  growth_strategy: 'Clear growth path attracts strategic buyers and premium multiples.',
  succession_plan: 'Well-defined succession plan demonstrates readiness and professionalism.',
  // Production CEPA keys
  financial: 'Solid financial capital attracts buyers and supports premium valuations.',
  human: 'Strong management depth reduces key-person risk and ensures continuity.',
  structural: 'Documented systems and processes increase transferability and buyer confidence.',
  customer: 'Diversified customer base reduces concentration risk and supports premium multiples.',
  wealth: 'Closing wealth gap means the sale will reasonably meet personal financial targets.',
};

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-500';
  if (score >= 60) return 'text-[hsl(var(--succeedo-orange))]';
  if (score >= 40) return 'text-amber-500';
  return 'text-red-500';
};

const getScoreBgColor = (score) => {
  if (score >= 80) return 'bg-green-500/10';
  if (score >= 60) return 'bg-[hsl(var(--succeedo-orange))]/10';
  if (score >= 40) return 'bg-amber-500/10';
  return 'bg-red-500/10';
};

const GapsVsStrengths = ({ categoryScores, className }) => {
  const sortedCategories = Object.entries(categoryScores || {}).sort(([, a], [, b]) => a - b);
  const gaps = sortedCategories.slice(0, 3);
  const strengths = [...sortedCategories]
    .reverse()
    .filter(([, score]) => score >= 70)
    .slice(0, 3);

  return (
    <div className={`space-y-8 ${className || ''}`}>
      <div>
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <h3 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">Top 3 Gaps</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {gaps.map(([category, score]) => {
            const delta = 70 - score;
            return (
              <div
                key={category}
                className={`p-5 rounded-[12px] border border-[hsl(var(--border))] ${getScoreBgColor(score)}`}>
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-[14px] font-semibold text-[hsl(var(--foreground))]">
                    {categoryLabels[category] || category}
                  </h4>
                  <div className={`text-[18px] font-semibold ${getScoreColor(score)}`}>{score}</div>
                </div>
                <div className="text-[12px] text-[hsl(var(--muted-foreground))] mb-3">
                  <span className="font-semibold">{delta} points</span> below target
                </div>
                <p className="text-[13px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                  {gapMeanings[category] || 'This dimension needs attention to improve sale-readiness.'}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {strengths.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <h3 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">Strengths</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {strengths.map(([category, score]) => (
              <div
                key={category}
                className={`p-5 rounded-[12px] border border-[hsl(var(--border))] ${getScoreBgColor(score)}`}>
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-[14px] font-semibold text-[hsl(var(--foreground))]">
                    {categoryLabels[category] || category}
                  </h4>
                  <div className={`text-[18px] font-semibold ${getScoreColor(score)}`}>{score}</div>
                </div>
                <p className="text-[13px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                  {strengthMeanings[category] || 'Continue maintaining this strong dimension.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

GapsVsStrengths.propTypes = {
  categoryScores: PropTypes.objectOf(PropTypes.number).isRequired,
  className: PropTypes.string,
};

GapsVsStrengths.defaultProps = {
  className: '',
};

export default GapsVsStrengths;
